import React from 'react';
import useCheckoutState from '../hooks/useCheckoutState';
import useAiTarget from '../hooks/useAiTarget';
import { getEventConfirmation } from './get-event-confirmation';
import MultilineText from '../components/MultilineText';
import ConfirmationHtml from '../components/ConfirmationHtml';
import Button from '../components/Button';
import {
  CalendarIcon,
  MapPinIcon,
  ShoppingBagIcon,
  TicketIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { formatEventDate } from '../lib/format-date';
import { useCheckoutLocalization } from '../hooks/useCheckoutLocalization';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import StyledElement from '../components/StyledElement';
import SuccessCheckmark from '../components/SuccessCheckmark';
import AddToCalendarButton from '../AddToCalendar/AddToCalendarButton';
import ConfirmationGuests, { previewGuestsView } from './ConfirmationGuests';
import { groupTicketsByType } from '@repo/lib';

const EventConfirmation = ({ payment, isConfirmation, preview }) => {
  const { restartPayment, checkout, seller, theme } = useCheckoutState();
  const previewGuests = preview ? previewGuestsView(checkout, payment) : null;
  const aiTarget = useAiTarget();

  const { locale } = useCheckoutLocalization();
  const labels = getThemeLabels(theme);
  const isDisabled = preview;

  // This payment has been canceled by the merchant.
  if (payment.orderStatus === 'canceled') {
    return (
      <div className="cp_status-wrapper text-center px-4 py-8 w-full">
        <div className="cp_status-content-wrapper">
          <h2 className="cp_status-title mb-2 text-[var(--cp_checkout-confirmation-heading-color)] [font-size:var(--cp_product-title-font-size)] [line-height:var(--cp_product-title-line-height)] [font-family:var(--cp_product-title-font-family)] [font-weight:var(--cp_product-title-font-weight)] [font-style:var(--cp_product-title-font-style)]">
            Booking canceled
          </h2>
          <div className="cp_status-content mb-4 text-[var(--cp_checkout-confirmation-text-color)]">
            Your booking has been canceled.
          </div>
        </div>
      </div>
    );
  }

  switch (payment.status) {
    case 'pending':
    case 'partially_paid':
    case 'unpaid':
    case 'refunded':
    case 'partial_refund':
    case 'paid': {
      const { title, message, type } = getEventConfirmation({
        checkout,
        charge: payment,
        seller,
        email: payment.customer.email,
      });

      return (
        <>
          <div
            className="cp_status-wrapper text-center px-4 py-8 w-full"
            {...aiTarget('confirmation')}
          >
            <div className="cp_status-content-wrapper">
              <SuccessCheckmark />
              <h2 className="cp_status-title mb-2 text-[var(--cp_checkout-confirmation-heading-color)] [font-size:var(--cp_product-title-font-size)] [line-height:var(--cp_product-title-line-height)] [font-family:var(--cp_product-title-font-family)] [font-weight:var(--cp_product-title-font-weight)] [font-style:var(--cp_product-title-font-style)]">
                {title}
              </h2>
              <div className="cp_status-content mb-4 text-[var(--cp_checkout-confirmation-text-color)]">
                {type === 'html' ? (
                  <ConfirmationHtml html={message} preview={preview} className="cp_status-text" />
                ) : (
                  <MultilineText className="cp_status-text">{message}</MultilineText>
                )}
              </div>

              {checkout.eventDetails && (
                <div className="cp_status-event-details text-center space-y-1 mb-4 text-[var(--cp_checkout-confirmation-text-color)]">
                  {checkout.showConfirmationEventName && (
                    <h2 className="cp_status-title text-[var(--cp_checkout-confirmation-heading-color)] [font-size:var(--cp_product-title-font-size)] [line-height:var(--cp_product-title-line-height)] [font-family:var(--cp_product-title-font-family)] [font-weight:var(--cp_product-title-font-weight)] [font-style:var(--cp_product-title-font-style)]">
                      {checkout.title}
                    </h2>
                  )}

                  {checkout.showConfirmationEventDateTime && checkout.eventDetails.startDate && (
                    <div className="cp_status-event-details-date-wrapper flex items-start gap-x-2 text-center justify-center">
                      <div className="cp_status-event-details-date-icon-wrapper mt-0.5">
                        <CalendarIcon className="cp_status-event-details-date-icon h-5 w-5" />
                      </div>
                      <div
                        className="cp_status-event-details-date flex flex-col"
                        style={{
                          fontWeight: 'calc(var(--cp_font-body-weight) + 100)',
                        }}
                      >
                        {formatEventDate({
                          startDate: checkout.eventDetails.startDate,
                          endDate: checkout.eventDetails.endDate,
                          timezone: checkout.eventDetails.timezone,
                          locale,
                        }).map(date => (
                          <span key={date}>{date}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {checkout.showConfirmationEventLocation &&
                    checkout.eventDetails.location &&
                    (checkout.eventDetails.type === 'in_person' ||
                      checkout.eventDetails.type === 'hybrid') && (
                      <div className="cp_status-event-details-location-wrapper flex items-center gap-x-2 text-center justify-center">
                        <div className="cp_status-event-details-location-icon-wrapper">
                          <MapPinIcon className="cp_status-event-details-location-icon h-5 w-5" />
                        </div>
                        {checkout.eventDetails.mapsLink && (
                          <a
                            href={checkout.eventDetails.mapsLink}
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            className="cp_status-event-details-location-link underline"
                          >
                            <span
                              className="cp_status-event-details-location"
                              style={{
                                fontWeight: 'calc(var(--cp_font-body-weight) + 100)',
                              }}
                            >
                              {checkout.eventDetails.location}
                            </span>
                          </a>
                        )}

                        {!checkout.eventDetails.mapsLink && (
                          <span
                            className="cp_status-event-details-location"
                            style={{
                              fontWeight: 'calc(var(--cp_font-body-weight) + 100)',
                            }}
                          >
                            {checkout.eventDetails.location}
                          </span>
                        )}
                      </div>
                    )}

                  {checkout.showConfirmationEventMeetingLink &&
                    (checkout.eventDetails.type === 'virtual' ||
                      checkout.eventDetails.type === 'hybrid') && (
                      <div
                        className={
                          'cp_event-details-virtual-wrapper flex items-center gap-x-2 text-center justify-center'
                        }
                      >
                        <div className="cp_event-details-virtual-icon-wrapper">
                          <VideoCameraIcon className="cp_event-details-virtual-icon h-5 w-5" />
                        </div>
                        <span
                          className="cp_event-details-virtual"
                          style={{
                            fontWeight: 'calc(var(--cp_font-body-weight) + 100)',
                          }}
                        >
                          {checkout.eventDetails.type === 'hybrid'
                            ? labels.hybridEventText
                            : labels.virtualEventText}
                        </span>
                      </div>
                    )}
                </div>
              )}

              {checkout.showConfirmationEventTickets &&
                payment.tickets &&
                payment.tickets.length > 0 && (
                  <div className="cp_event-details-tickets-purchased text-[var(--cp_checkout-confirmation-text-color)]">
                    <h4 className="cp_event-details-tickets-purchased-heading [font-size:var(--cp_font-heading-size-h4)] [line-height:var(--cp_font-heading-line-height-h4)] text-[var(--cp_checkout-confirmation-heading-color)]">
                      {labels.ticketsPurchasedText || 'Tickets purchased'}
                    </h4>
                    {groupTicketsByType(payment.tickets).map(ticket => (
                      <React.Fragment key={ticket.ticketType}>
                        <div className="cp_event-details-tickets-purchased-wrapper flex items-center gap-x-2 text-center justify-center">
                          <TicketIcon className="cp_event-details-tickets-purchased-tickets-icon h-5 w-5" />
                          <span
                            className="cp_event-details-tickets-purchased-tickets"
                            style={{
                              fontWeight: 'calc(var(--cp_font-body-weight) + 100)',
                            }}
                          >
                            {ticket.quantity}x {ticket.name}
                          </span>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}

              {checkout.showConfirmationEventAddOns && payment.orderBumps?.length > 0 && (
                <div className="cp_event-details-add-ons-purchased mt-4 text-[var(--cp_checkout-confirmation-text-color)]">
                  <h4 className="cp_event-details-add-ons-purchased-heading [font-size:var(--cp_font-heading-size-h4)] [line-height:var(--cp_font-heading-line-height-h4)] text-[var(--cp_checkout-confirmation-heading-color)]">
                    Add-ons
                  </h4>
                  {payment.orderBumps.map(bump => (
                    <div
                      key={`${bump.product}-${bump.productPriceId ?? ''}`}
                      className="cp_event-details-add-ons-purchased-wrapper flex items-center gap-x-2 text-center justify-center"
                    >
                      <ShoppingBagIcon className="cp_event-details-add-ons-purchased-icon h-5 w-5" />
                      <span
                        className="cp_event-details-add-ons-purchased-item"
                        style={{
                          fontWeight: 'calc(var(--cp_font-body-weight) + 100)',
                        }}
                      >
                        {bump.quantity}x {bump.productTitle}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {checkout.showConfirmationAddToCalendar && (
                <AddToCalendarButton
                  calendar={payment.calendar}
                  label={labels.addToCalendarText}
                  preview={preview}
                />
              )}
            </div>
          </div>
          {previewGuests && <ConfirmationGuests payment={payment} preview={previewGuests} />}
          {!preview && payment.attendeeStatus && <ConfirmationGuests payment={payment} />}
        </>
      );
    }

    case 'failed':
    case 'canceled':
      return (
        <div className="cp_status-wrapper text-center px-4 py-8 w-full">
          <div className="cp_status-content-wrapper">
            <h2 className="cp_status-title mb-2 text-[var(--cp_checkout-confirmation-heading-color)] [font-size:var(--cp_product-title-font-size)] [line-height:var(--cp_product-title-line-height)] [font-family:var(--cp_product-title-font-family)] [font-weight:var(--cp_product-title-font-weight)] [font-style:var(--cp_product-title-font-style)]">
              Payment failed
            </h2>
            <div className="cp_status-content mb-4 text-[var(--cp_checkout-confirmation-text-color)]">
              Your payment failed and has not been processed.
              <br />
              Please try again.
            </div>
          </div>
          <StyledElement
            elementPath="checkout.form.button"
            as={Button}
            handleClick={() => {
              restartPayment({ isConfirmation });
            }}
            appearance="submit"
            className={'cp_button whitespace-nowrap leading-snug'}
            size="full"
            disabled={isDisabled}
          >
            Try again
          </StyledElement>
        </div>
      );
    default:
      return (
        <div className="cp_status-wrapper text-center px-4 py-8 w-full">
          <div className="cp_status-content-wrapper">
            <h2 className="cp_status-title mb-2 text-[var(--cp_checkout-confirmation-heading-color)] [font-size:var(--cp_product-title-font-size)] [line-height:var(--cp_product-title-line-height)] [font-family:var(--cp_product-title-font-family)] [font-weight:var(--cp_product-title-font-weight)] [font-style:var(--cp_product-title-font-style)]">
              Payment pending
            </h2>
            <div className="cp_status-content mb-4 text-[var(--cp_checkout-confirmation-text-color)]">
              Your payment has been received and is awaiting payment confirmation. Please contact{' '}
              {seller.displayName || 'the seller'} to confirm your payment.
            </div>
          </div>
        </div>
      );
  }
};

export default EventConfirmation;
